<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import TypingIndicator from '@/components/atoms/typing/TypingIndicator.vue'
import Button  from 'primevue/button'
import { RefreshCw, Send, SendHorizonal } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import {
  useCampaignService,
  type ICampaignKnowledgeChatSource,
} from '@/services/campaign/useCampaignService'

type KnowledgeChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  sources?: ICampaignKnowledgeChatSource[]
  warning?: string | null
}

const props = defineProps<{
  campaignId?: string | null
  context: string
  readyDocumentsCount: number
}>()

const visible = defineModel<boolean>({ default: false })

const campaignService = useCampaignService()
const { t } = useI18n()
const draft = ref('')
const loading = ref(false)
const messages = ref<KnowledgeChatMessage[]>([])
const messagesElement = ref<HTMLElement | null>(null)

const storageKey = computed(() =>
  props.campaignId ? `knowledge-chat:${props.context}:${props.campaignId}` : '',
)

const uniqueSources = (sources: ICampaignKnowledgeChatSource[] = []) => {
  const seen = new Set<string>()
  return sources.filter((source) => {
    const key = source.document_id || source.title
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const persistMessages = () => {
  if (!storageKey.value) return
  localStorage.setItem(storageKey.value, JSON.stringify(messages.value))
}

const loadMessages = () => {
  if (!storageKey.value) {
    messages.value = []
    return
  }

  try {
    const raw = localStorage.getItem(storageKey.value)
    const parsed = raw ? JSON.parse(raw) : []
    messages.value = Array.isArray(parsed) ? parsed : []
  } catch {
    messages.value = []
  }
}

const scrollToBottom = async () => {
  await nextTick()
  messagesElement.value?.scrollTo({
    top: messagesElement.value.scrollHeight,
    behavior: 'smooth',
  })
}

const sendQuestion = async (questionFromCard?: string) => {
  const question = (questionFromCard ?? draft.value).trim()
  if (!props.campaignId || !question || loading.value || props.readyDocumentsCount === 0) return

  visible.value = true
  draft.value = ''
  messages.value.push({
    id: crypto.randomUUID(),
    role: 'user',
    content: question,
  })
  persistMessages()

  loading.value = true
  await scrollToBottom()

  try {
    const response = await campaignService.askCampaignKnowledgeQuestion(props.campaignId, question)
    messages.value.push({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: response.answer,
      sources: uniqueSources(response.sources),
      warning: response.warning,
    })
  } catch {
    messages.value.push({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: t('ai.knowledge_chat.request_failed'),
      warning: 'knowledge_chat_request_failed',
    })
  } finally {
    loading.value = false
    persistMessages()
    void scrollToBottom()
  }
}

defineExpose({
  sendQuestion,
})

watch(storageKey, () => {
  draft.value = ''
  loadMessages()
}, { immediate: true })

watch(visible, (isVisible) => {
  if (isVisible) void scrollToBottom()
})
</script>

<template>
  <AppDialog
    v-model:modelValue="visible"
    :header="t('ai.knowledge_chat.title')"
    custom-class="backdrop-blur-xl !backdrop-saturate-150"
    :modal="false"
    :draggable="true"
    :dismissable-mask="false"
    width="28rem"
    :breakpoints="{ '575px': '94vw' }"
  >
    <section class="flex h-[70vh] flex-col gap-3">
      <div
        ref="messagesElement"
        class="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-md border border-[var(--p-surface-200)]/50  p-3 dark:border-[var(--p-primary-900)]/80 bg-white/80 dark:bg-[var(--p-primary-950)]/40!"
      >
        <p
          v-if="!messages.length"
          class="rounded-2xl border text-center border-dashed border-[var(--p-primary-300)] bg-[var(--p-primary-50)]/50 p-3 text-xs text-[var(--p-primary-700)] dark:border-[var(--p-primary-700)] dark:bg-[var(--p-primary-950)]/60 dark:text-[var(--p-primary-200)]"
        >
          {{ t('ai.knowledge_chat.empty_hint') }}
        </p>

        <div
          v-for="message in messages"
          :key="message.id"
          class="flex"
          :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm leading-5"
            :class="
              message.role === 'user'
                ? 'border border-transparent dark:border-[var(--p-primary-200)]/20! shadow-sm bg-[var(--p-primary-color)]/80  dark:bg-[var(--p-primary-500)]/60 text-white'
                : 'border border-transparent dark:border-[var(--p-primary-200)]/20! shadow-sm bg-[var(--p-surface-0)] text-[var(--p-primary-800)] dark:border-[var(--p-primary-700)] dark:bg-[var(--p-primary-900)]/70 dark:text-[var(--p-primary-100)]'
            "
          >
            {{ message.content }}
            <div v-if="message.sources?.length" class="mt-2 flex flex-wrap gap-1">
              <span
                v-for="source in uniqueSources(message.sources)"
                :key="`${message.id}-${source.document_id}`"
                class="rounded-full bg-[var(--p-primary-100)] px-2 py-0.5 text-[10px] font-medium text-[var(--p-primary-700)] ring-1 ring-[var(--p-primary-200)] dark:bg-[var(--p-primary-900)]/70 dark:text-[var(--p-primary-200)] dark:ring-[var(--p-primary-700)]"
              >
                {{ source.title }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="loading" class="flex justify-start">
          <TypingIndicator />
        </div>
      </div>

      <form class="flex items-end gap-2" @submit.prevent="sendQuestion()">
        <textarea
          v-model="draft"
          rows="1"
          class="min-w-0 flex-1 max-h-26! overflow-y-auto!  resize-none rounded-lg border border-[var(--p-primary-200)] bg-[var(--p-primary-0)] px-3 py-2 text-sm text-[var(--p-primary-800)] outline-none transition focus:border-[var(--p-primary-color)] dark:border-[var(--p-primary-700)] dark:bg-[var(--p-primary-950)] dark:text-[var(--p-primary-100)]"
          :placeholder="t('ai.knowledge_chat.question_placeholder')"
          :disabled="loading || readyDocumentsCount === 0"
          @keydown.enter.exact.prevent="sendQuestion()"
        />
        <Button
        text
          type="submit"
          class="grid rounded-full! !h-10 !w-10 p-0! shrink-0 place-items-center rounded-md bg-[var(--p-primary-color)] text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="loading || !draft.trim() || readyDocumentsCount === 0"
          :aria-label="t('ai.cards_panel.send_question')"
        >
          <SendHorizonal disabled="!loading" :size="20" class="dark:!text-[var(--p-primary-50)]" />
        </Button>
      </form>
    </section>
  </AppDialog>
</template>
